import React, { useState, useCallback, useEffect } from "react";
import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "./dialog";
import { Button } from "./button";
import { Typography } from "./typography";
import { Calendar } from "./calender";
import { TimeSlotButton } from "./time-slot-button";
import { TIME_SLOTS } from "../../constants/autostack";
import { TimeSlot } from "../../types/asset";

interface DateTimePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDateTimeSelect: (date: Date, time: TimeSlot) => void;
  selectedDate?: Date;
  selectedTime?: TimeSlot;
  title?: string;
  disablePastDates?: boolean;
}

export const DateTimePickerModal: React.FC<DateTimePickerModalProps> =
  React.memo(
    ({
      isOpen,
      onClose,
      onDateTimeSelect,
      selectedDate,
      selectedTime,
      title = "Start date and time",
      disablePastDates = false,
    }) => {
      const [tempDate, setTempDate] = useState<Date | undefined>(
        selectedDate || new Date()
      );
      const [tempTime, setTempTime] = useState<TimeSlot>(
        selectedTime || TIME_SLOTS[0]
      );
      const [currentMonth, setCurrentMonth] = useState<Date>(
        selectedDate || new Date()
      );

      useEffect(() => {
        if (isOpen) {
          const newDate = selectedDate || new Date();
          setTempDate(newDate);
          setTempTime(selectedTime || TIME_SLOTS[0]);
          setCurrentMonth(newDate);
        }
      }, [isOpen, selectedDate, selectedTime]);

      const handleDateSelect = useCallback((date: Date | undefined) => {
        if (date) {
          setTempDate(date);
        }
      }, []);

      const handleTimeSelect = useCallback((time: TimeSlot) => {
        setTempTime(time);
      }, []);

      const handleMonthChange = useCallback((month: Date) => {
        setCurrentMonth(month);
      }, []);

      const handleSave = useCallback(() => {
        if (tempDate && tempTime) {
          onDateTimeSelect(tempDate, tempTime);
          onClose();
        }
      }, [tempDate, tempTime, onDateTimeSelect, onClose]);

      const handleCancel = useCallback(() => {
        // Reset to original values
        setTempDate(selectedDate || new Date());
        setTempTime(selectedTime || TIME_SLOTS[0]);
        onClose();
      }, [selectedDate, selectedTime, onClose]);

      const getDisabledDate = useCallback(() => {
        if (!disablePastDates) return undefined;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return { before: today };
      }, [disablePastDates]);

      const isTimeSlotDisabled = useCallback(
        (timeSlot: string) => {
          if (!tempDate) return false;

          const today = new Date();
          const selectedDateOnly = new Date(tempDate);
          selectedDateOnly.setHours(0, 0, 0, 0);
          const todayDateOnly = new Date(today);
          todayDateOnly.setHours(0, 0, 0, 0);

          // Only disable if the selected date is today
          if (selectedDateOnly.getTime() !== todayDateOnly.getTime()) {
            return false;
          }

          const [hourStr, minuteStr] = timeSlot.split(":");
          const hours = parseInt(hourStr, 10);
          const minutes = parseInt(minuteStr, 10);

          const timeSlotDate = new Date();
          timeSlotDate.setHours(hours, minutes, 0, 0);

          return timeSlotDate <= today;
        },
        [tempDate]
      );

      const modifiers = {
        disabled: getDisabledDate(),
      };

      const modifiersClassNames = {
        disabled:
          "text-gray-500 opacity-40 cursor-not-allowed pointer-events-none bg-gray-50",
      };

      return (
        <Dialog open={isOpen} onOpenChange={onClose}>
          <DialogContent className="sm:max-w-4xl">
            <DialogHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <DialogTitle className="text-xl font-semibold">
                {title}
              </DialogTitle>
              <DialogClose
                className="rounded-full border border-gray-200 p-2 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                aria-label="Close modal"
              >
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </DialogHeader>

            <div className="flex flex-col lg:flex-row gap-8">
              {/* Calendar Section */}
              <div className="flex-1 relative">
                <Calendar
                  mode="single"
                  selected={tempDate}
                  onSelect={handleDateSelect}
                  disabled={getDisabledDate()}
                  modifiers={modifiers}
                  modifiersClassNames={modifiersClassNames}
                  month={currentMonth}
                  onMonthChange={handleMonthChange}
                  initialFocus
                  className="rounded-md border w-full"
                  classNames={{
                    months:
                      "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                    month:
                      "space-y-4 w-full relative flex flex-col items-center text-center !ml-0",
                    caption:
                      "flex justify-center pt-1 relative items-center mb-4 mt-2",
                    caption_label: "text-lg font-medium",
                    nav: "absolute z-10 top-4 left-0 right-0 px-12 flex justify-between items-center w-full",
                    nav_button:
                      "h-8 w-8 bg-transparent p-0 opacity-50 hover:opacity-100 border border-gray-200 rounded-md transition-opacity",
                    table: "w-full border-collapse space-y-1",
                    head_row: "flex",
                    head_cell:
                      "text-gray-500 rounded-md w-10 font-medium text-sm text-center",
                    row: "flex w-full mt-2",
                    cell: "h-12 w-12 text-center text-sm p-0 relative",
                    day: "h-12 w-12 p-0 font-normal rounded-md hover:bg-gray-100 transition-colors",
                    day_selected: "bg-pink-500 text-white hover:bg-pink-600",
                    day_today: "bg-gray-100 text-gray-900 font-medium",
                    day_outside: "text-gray-400",
                  }}
                />
              </div>

              {/* Time Slots Section */}
              <div className="flex-1">
                <div className="space-y-4">
                  <Typography variant="h3" className="text-lg font-semibold">
                    Time
                  </Typography>
                  <div
                    className="grid grid-cols-3 gap-3"
                    role="radiogroup"
                    aria-label="Select time"
                  >
                    {TIME_SLOTS.map((time) => (
                      <TimeSlotButton
                        key={time}
                        time={time}
                        isSelected={tempTime === time}
                        onClick={handleTimeSelect}
                        variant="modal"
                        disabled={isTimeSlotDisabled(time)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between gap-6 px-24 pt-6">
              <Button
                type="button"
                variant="ghost"
                onClick={handleCancel}
                className="px-8 py-6 border border-gray-200 rounded-full w-full"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                className="btn-primary px-8 py-6 w-full"
                disabled={!tempDate || !tempTime}
              >
                Save
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      );
    }
  );

DateTimePickerModal.displayName = "DateTimePickerModal";
